import SectionPanel from '@/components/SectionPanel';
import { useOrganization } from "src/auth/AuthContext";
import { SimpleGrid, Textarea } from "@chakra-ui/react"
import type { JSX } from 'react';
import ErrorMessage from "src/components/ErrorMessage";
import { useForm } from "react-hook-form";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Commitment } from 'src/api/Commitment';
import DateSelectController from '@/components/fields/DateSelect';
import { useProjectNames } from '@/hooks/useProjectNames';
import { commitmentService } from "src/service/CommitmentService";
import CancelButton from "src/components/buttons/CancelButton";
import SubmitButton from "@/components/buttons/SubmitButton";
import { Field } from "@/components/ui/field";
import { AmountInputController } from "@/components/fields/AmountInput";
import { ProjectSelectController } from "@/components/fields/ProjectSelect";
import FundSourceController from "@/components/fields/FundSourceController";
import FormPage from '@/components/FormPage';
import { useI18n } from '@/i18n/I18nContext';

export interface CommitmentsFormProps {
    commitmentId?: number;
    organizationId: number;
    onSubmit: (ps: Commitment) => Promise<void>;
    viewOnly?: boolean;
    header: string;
}

const CommitmentsForm = ({ commitmentId, organizationId, onSubmit, viewOnly, header }: CommitmentsFormProps): JSX.Element => {
    const { t } = useI18n();
    const organization = useOrganization();
    const { control, register, handleSubmit, reset, formState: { isSubmitting } } = useForm<Commitment>({
        defaultValues: {
            id: commitmentId,
            projectId: -1,
            fundSourceName: '',
            amount: 0,
            entryDate: new Date(),
            comments: '',
            organizationId: organizationId
        }
    });
    const [serverError, setServerError] = useState('');
    const { options: projectNames } = useProjectNames();
    const [institutions, setInstitutions] = useState<string[]>([]); // unique names for institutes
    const navigate = useNavigate();

    useEffect(() => {
        if (commitmentId > -1) {
            commitmentService.find(commitmentId).then(p => reset(p));
        }
    }, [commitmentId, reset]);

    useEffect(() => {
        let b = commitmentService.fundSourceNames().then(setInstitutions);
        Promise.all([b]);
    }, []);

    const readOnly = viewOnly ?? false;
    console.info(`form is readonly: ${readOnly}`);

    const onSubmitInternal = (c: Commitment): Promise<void> => {
        console.info(`submitting ${JSON.stringify(c)}`);
        return onSubmit(c)
            .then(() => navigate('/fundraising/commitments'))
            .catch(_err => setServerError('Fehler'));
    };

    return (
        <FormPage title={header} description={t('forms.commitments.description')}
            onSubmit={handleSubmit(onSubmitInternal)} invalid={!!serverError}
            actions={<>
                <CancelButton onClick={() => navigate('/fundraising/commitments/')} />
                {!readOnly && <SubmitButton isSubmitting={isSubmitting} />}
            </>}>
            {serverError && <ErrorMessage message={serverError} />}
            <SectionPanel title={t('forms.commitments.details')}>
                <ProjectSelectController control={control} projectNames={projectNames} name='projectId' label='Projekt' readOnly={readOnly} required/>

                <FundSourceController
                    control={control}
                    name='fundSourceName'
                    label='Institution (oder neue Institution erstellen)'
                    readOnly={readOnly}
                    options={institutions}
                    required
                />

                <SimpleGrid columns={{ base: 1, md: 2 }} gap='4'>
                    <DateSelectController label='Datum' control={control} name='entryDate' clearable={false} required disabled={readOnly} />

                    <AmountInputController control={control} name='amount' label={`Summe (${organization?.currency ?? '—'})`} readOnly={readOnly} />
                </SimpleGrid>

                <Field id="comments" label='Bemerkungen' disabled={readOnly}>
                    <Textarea {...register('comments')} minH='7rem' resize='vertical' />
                </Field>

            </SectionPanel>
        </FormPage>
    );
}

export default CommitmentsForm
